/* ════════════════════════════════════════════════════════════════════
   src/auth/roles.js — dual-role support (B144)

   A doctor who is also assigned Clinic Admin has ONE login:
   role = clinicadmin, profile_type = doctor. The backend keeps their doctor
   roster row and slots untouched, so they can BOTH practise (bookable,
   consultations) and administer their clinic. The stored `role` is the
   primary landing role; this module lets such a user switch which app they
   are looking at without logging out.

   The doctor API endpoints resolve the doctor from the JWT profile
   (user.profile_id), not from the role string, so the same login already has
   full doctor access on the server. The switch is therefore a client-side
   view change — no re-auth, no second account.
   ════════════════════════════════════════════════════════════════════ */

const ACTIVE_ROLE_KEY = '1ayurved_active_role';

/* Base path each role lands on — mirrors RoleRedirect in App.jsx. */
export const ROLE_HOME = {
  superadmin:  '/dashboard',
  clinicadmin: '/clinic',
  doctor:      '/doctor',
  reception:   '/desk',
  accountant:  '/desk',
  masseur:     '/desk/masseur',
  pharmacist:  '/desk/pharmacy',
  rmo:         '/desk/rmo',
  assistant:   '/desk/pk-consumables',
  patient:     '/my',
};

export function roleHome(role) {
  return ROLE_HOME[role] || '/clinics';
}

/* Short, human labels for the switcher chips. */
export const ROLE_LABEL = {
  superadmin:  'Super Admin',
  clinicadmin: 'Clinic Admin',
  doctor:      'Doctor',
  reception:   'Reception',
  accountant:  'Accounts',
  masseur:     'Masseur',
  pharmacist:  'Pharmacy',
  rmo:         'RMO',
  assistant:   'Assistant',
  patient:     'Patient',
};

/**
 * The set of roles this login may act as.
 *
 * The primary role is always included. A clinicadmin who also holds a doctor
 * profile (profile_type = doctor → user.doctorId is present in the payload)
 * may additionally act as a doctor. The reverse branch is kept for
 * completeness but stays inert today, because provisioning flips a
 * doctor-turned-admin to clinicadmin-primary (see OnboardingController).
 */
export function availableRoles(user) {
  if (!user || !user.role) return [];

  // Preferred: the server tells us exactly which roles this login may act as
  // (User::actingRoles). This is authoritative and covers both directions
  // (clinicadmin-primary who is also a doctor, and doctor-primary who is also
  // the clinic admin).
  if (Array.isArray(user.roles) && user.roles.length) {
    const known = new Set(Object.keys(ROLE_HOME));
    const roles = user.roles.filter((r) => known.has(r));
    // Guarantee the primary role is present and listed first.
    if (!roles.includes(user.role)) roles.unshift(user.role);
    return roles.filter((r, i) => roles.indexOf(r) === i);
  }

  // Fallback heuristic for an older cached payload (pre-B144), used only until
  // the /me refresh lands the roles array.
  const roles = [user.role];
  const hasDoctorProfile = !!user.doctorId;
  if (user.role === 'clinicadmin' && hasDoctorProfile) {
    roles.push('doctor');
  } else if (user.role === 'doctor' && hasDoctorProfile && user.isClinicAdmin) {
    roles.push('clinicadmin');
  }
  return roles.filter((r, i) => roles.indexOf(r) === i);
}

export function canSwitchRoles(user) {
  return availableRoles(user).length > 1;
}

/**
 * The role the app should currently render as. Falls back to the primary role
 * whenever the stored choice isn't one the user actually holds — this keeps
 * the switch tamper-safe: writing a role into localStorage that the login
 * doesn't own is simply ignored.
 */
export function activeRole(user) {
  const roles = availableRoles(user);
  if (roles.length === 0) return null;

  let stored = null;
  try { stored = localStorage.getItem(ACTIVE_ROLE_KEY); } catch { /* storage off */ }

  return stored && roles.includes(stored) ? stored : user.role;
}

export function setActiveRole(role) {
  try { localStorage.setItem(ACTIVE_ROLE_KEY, role); } catch { /* storage off */ }
}

export function clearActiveRole() {
  try { localStorage.removeItem(ACTIVE_ROLE_KEY); } catch { /* storage off */ }
}
